import { ApiService } from "./api.service";
import type {
  TMyWorkAvailability,
  TMyWorkFeedResponse,
  TMyWorkMentionsResponse,
  TMyWorkSummary,
} from "@/types/my-work";

/** Reads the My Work unified feed + summary (assigned tasks + events + owned requests). */
export class MyWorkService extends ApiService {
  /**
   * `order_by` takes a My Work column token (`-` prefixed for descending) and
   * sorts the WHOLE feed before the server pages it — build it with
   * `myWorkOrderBy`. Sorting the returned rows instead reorders one page rather
   * than the user's workload (NG-493).
   */
  async fetchMyWork(
    slug: string,
    params: { page?: number; per_page?: number; order_by?: string } = {},
  ): Promise<TMyWorkFeedResponse> {
    return this.get(`/api/workspaces/${slug}/my-work/`, { params });
  }

  async fetchMyWorkSummary(slug: string): Promise<TMyWorkSummary> {
    return this.get(`/api/workspaces/${slug}/my-work/summary/`);
  }

  async fetchMyWorkAvailability(slug: string): Promise<TMyWorkAvailability> {
    return this.get(`/api/workspaces/${slug}/my-work/availability/`);
  }

  /**
   * `unread: true` narrows the feed to mentions the caller has not read. The
   * narrowing is the SERVER's job, not the caller's: the endpoint caps the feed
   * at 30 rows, so filtering the response would hide an unread mention sitting
   * behind 30 newer read ones (NG-651). My Work's card asks for the narrowed
   * feed; its View-all drawer omits the flag and gets the full history.
   */
  async fetchMyWorkMentions(
    slug: string,
    params: { unread?: boolean } = {},
  ): Promise<TMyWorkMentionsResponse> {
    return this.get(`/api/workspaces/${slug}/my-work/mentions/`, { params });
  }
}
